// Dashboard.js (User Metrics)
import { useState, useEffect } from "react";
import { doc, getDoc, collection, query, where, onSnapshot } from "firebase/firestore";
import { auth, db } from "../../shared/services/firebase";
import "./Dashboard.css";
import { getAuth, signOut } from "firebase/auth";
import { useNavigate } from "react-router-dom";

export default function Dashboard() {
  const [feedbackList, setFeedbackList] = useState([]);
  const [userUUID, setUserUUID] = useState("");
  const [copySuccess, setCopySuccess] = useState("");
  const [debugMsg, setDebugMsg] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    let unsubscribe = null;
    const fetchData = async () => {
      if (!auth.currentUser) {
        setDebugMsg("No authenticated user found.");
        return;
      }
      setDebugMsg(`Current UID: ${auth.currentUser.uid}`);
      // Fetch user document directly by UID
      const userDocRef = doc(db, "users", auth.currentUser.uid);
      const userDoc = await getDoc(userDocRef);
      if (!userDoc.exists()) {
        setDebugMsg(`No user document found for UID: ${auth.currentUser.uid}`);
        return;
      }
      const userData = userDoc.data();
      if (!userData.uuid) {
        setDebugMsg("User document found, but no uuid field present.");
        return;
      }
      setUserUUID(userData.uuid);
      setDebugMsg("");
      // Real-time feedback updates
      const feedbackQuery = query(collection(db, "feedback"), where("uuid", "==", userData.uuid));
      unsubscribe = onSnapshot(feedbackQuery, (feedbackSnapshot) => {
        // Show most recent feedback at the top
        const feedbacks = feedbackSnapshot.docs.map(doc => doc.data());
        feedbacks.sort((a, b) => {
          const aTime = a.createdAt && a.createdAt.toDate ? a.createdAt.toDate().getTime() : 0;
          const bTime = b.createdAt && b.createdAt.toDate ? b.createdAt.toDate().getTime() : 0;
          return bTime - aTime;
        });
        setFeedbackList(feedbacks);
      });
    };
    fetchData();
    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, []);

  const feedbackUrl = userUUID ? `${window.location.origin}/feedback/${userUUID}` : "";

  const handleCopy = () => {
    if (feedbackUrl) {
      navigator.clipboard.writeText(feedbackUrl);
      setCopySuccess("Copied!");
      setTimeout(() => setCopySuccess(""), 1500);
    }
  };

  const handleLogout = async () => {
    await signOut(getAuth());
    navigate("/", { replace: true });
  };

  // --- Statistics ---
  const totalFeedback = feedbackList.length;
  // Only consider feedbacks with a numeric rating for rating stats
  const ratingFeedbacks = feedbackList.filter(f => typeof f.rating === 'number' && !isNaN(f.rating));
  const avgRating = ratingFeedbacks.length > 0 ? (
    ratingFeedbacks.reduce((sum, f) => sum + Number(f.rating), 0) / ratingFeedbacks.length
  ).toFixed(2) : "-";
  const uniqueEmails = new Set(feedbackList.map(f => f.email).filter(Boolean)).size;
  // Rating distribution for 1-5
  const ratingCounts = [1,2,3,4,5].map(r => ratingFeedbacks.filter(f => Number(f.rating) === r).length);
  const maxCount = Math.max(...ratingCounts, 1);

  return (
    <div className="dashboard-container">
      <div className="dashboard-header">
        <div className="dashboard-header-main">
          <h1 className="dashboard-title">Simplest Feedback</h1>
          <div className="dashboard-subtitle">Your personal feedback hub</div>
        </div>
        <div className="dashboard-header-actions">
          <button className="dashboard-logout-btn" onClick={handleLogout}>Logout</button>
        </div>
        <div className="dashboard-stats-panel">
          <div className="dashboard-stat">
            <div className="dashboard-stat-label">Total Feedback</div>
            <div className="dashboard-stat-value">{totalFeedback}</div>
          </div>
          <div className="dashboard-stat">
            <div className="dashboard-stat-label">Average Rating</div>
            <div className="dashboard-stat-value">{avgRating}</div>
          </div>
          <div className="dashboard-stat">
            <div className="dashboard-stat-label">Unique Emails</div>
            <div className="dashboard-stat-value">{uniqueEmails}</div>
          </div>
        </div>
      </div>
      <div className="dashboard-chart-section">
        <div className="dashboard-chart-title">Rating Distribution</div>
        {ratingFeedbacks.length === 0 ? (
          <div style={{ color: '#888', margin: '1em 0' }}>No rating feedback yet</div>
        ) : (
          <svg className="dashboard-bar-chart" width="100%" height="80" viewBox="0 0 220 80">
            {ratingCounts.map((count, i) => (
              <g key={i}>
                <rect
                  x={20 + i * 40}
                  y={80 - (count / maxCount) * 60}
                  width="24"
                  height={(count / maxCount) * 60}
                  fill="#1976d2"
                  rx="5"
                />
                <text x={32 + i * 40} y="75" textAnchor="middle" fontSize="13" fill="#555">{i+1}</text>
                <text x={32 + i * 40} y={80 - (count / maxCount) * 60 - 6} textAnchor="middle" fontSize="12" fill="#1976d2">{count}</text>
              </g>
            ))}
          </svg>
        )}
      </div>
      <div className="dashboard-content">
        {debugMsg && <div style={{ color: "red", marginBottom: "1em" }}>{debugMsg}</div>}
        {userUUID && (
          <div className="dashboard-share-link">
            <strong>Share this link to collect feedback!</strong>
            <div className="dashboard-share-link-row">
              <input type="text" value={feedbackUrl} readOnly style={{ width: "350px" }} />
              <button onClick={handleCopy}>Copy</button>
              {copySuccess && <span style={{ color: "green" }}>{copySuccess}</span>}
              <a href="/select-form" style={{ marginLeft: '1em' }}>
                <button type="button" className="dashboard-change-form-btn">Change Form Type</button>
              </a>
            </div>
            <div style={{ marginTop: "0.5em" }}>
              <a href={feedbackUrl} target="_blank" rel="noopener noreferrer" style={{ fontWeight: "bold", color: "#1976d2", textDecoration: "underline" }}>
                {feedbackUrl}
              </a>
            </div>
          </div>
        )}
        <div className="metrics">
          <h3>Feedback Log</h3>
          <div className="feedback-list">
            {feedbackList.length === 0 ? (
              <div className="feedback-empty">No feedback yet.</div>
            ) : (
              feedbackList.map((item, index) => (
                <div key={index} className="feedback-log-card">
                  <div className="feedback-log-header">
                    {/* Show relevant summary info based on feedback type */}
                    {item.type === 'star-rating' && (
                      <span className="feedback-log-rating">Rating: <strong>{item.rating ?? "-"}</strong></span>
                    )}
                    {item.type === 'nps-survey' && (
                      <span className="feedback-log-rating">NPS: <strong>{item.nps ?? "-"}</strong></span>
                    )}
                    {item.type === 'emoji-feedback' && (
                      <span className="feedback-log-rating">Emoji: <strong>{item.emoji ?? "-"}</strong></span>
                    )}
                    {item.type === 'multiple-choice' && (
                      <span className="feedback-log-rating">Choice: <strong>{item.choice ?? "-"}</strong></span>
                    )}
                    {item.type === 'thumbs-feedback' && (
                      <span className="feedback-log-rating">Thumb: <strong>{item.thumb === 'up' ? '👍' : item.thumb === 'down' ? '👎' : '-'}</strong></span>
                    )}
                    {(!item.type || item.type === 'simple-feedback') && (
                      <span className="feedback-log-rating">Feedback</span>
                    )}
                    <span className="feedback-log-date">{item.createdAt && item.createdAt.toDate ? item.createdAt.toDate().toLocaleString() : ""}</span>
                  </div>
                  <div className="feedback-log-body">
                    {/* Show main feedback/comment/description */}
                    {item.description && <div className="feedback-log-description">{item.description}</div>}
                    {item.comments && <div className="feedback-log-description">{item.comments}</div>}
                  </div>
                  <div className="feedback-log-footer">
                    <span className="feedback-log-name">{item.name ? `From: ${item.name}` : ""}</span>
                    <span className="feedback-log-email">{item.email ? `Email: ${item.email}` : ""}</span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
